import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Row,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { getMafiaEmpId } from "./mafiaIdentity";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";


export default function FrmSelect({
  partnum = "",
  revMajor = 0,
  revMinor = 0,
  createdBy = "",
  onBack,
  onFinish,
}) {
  const [engineers, setEngineers] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [currentUsername, setCurrentUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");
  const dialog = useAppDialog();

  const mountedRef = useRef(true);

  const reviewSummary = useMemo(() => {
    return {
      createdBy: createdBy || "—",
      sendEmail: sendEmail ? "Yes" : "No",
      selectedEngineer: sendEmail
        ? (engineers.find((eng) => eng.empId === selectedEngineer)?.name || selectedEngineer || "Not selected")
        : "Not required",
      targetRevision: `${revMajor}.${revMinor}`,
    };
  }, [createdBy, sendEmail, selectedEngineer, revMajor, revMinor, engineers]);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      const [username, data] = await Promise.all([
        getCurrentUsername(),
        getActiveEngineers(),
      ]);

      if (!mountedRef.current) return;

      setCurrentUsername(username);

      // Legacy frmSelect.vb lines 7-21: exclude the current user from the
      // engineer combo box (don't email yourself), with a legacy carve-out
      // that let `joseph.regan` see themselves. The carve-out was a
      // developer convenience tied to a specific Windows login and is now
      // dead code in a server-rendered, EmpId-keyed world — removed.
      // Go-live parity (2026-09-24, audit S4): the server now returns
      // { empId, name } for every active MAFIA level 1-4 user (caller already
      // excluded); plain strings from an older API are still accepted.
      const me = String(username || "").trim().toLowerCase();
      const filtered = (data || [])
        .map((eng) =>
          typeof eng === "string"
            ? { empId: eng.trim(), name: eng.trim() }
            : {
                empId: String(eng?.empId ?? eng?.EmpId ?? "").trim(),
                name: String(eng?.name ?? eng?.Name ?? "").trim(),
              }
        )
        .filter((eng) => eng.empId !== "" && eng.empId.toLowerCase() !== me);

      setEngineers(filtered);
    } catch (error) {
      console.error("Failed to load frmSelect:", error);
      if (mountedRef.current) {
        setPageError("Failed to load engineer selection.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadPage();

    return () => {
      mountedRef.current = false;
    };
  }, [loadPage]);

  async function handleFinish() {
    try {
      setSubmitting(true);
      setPageError("");

      if (sendEmail) {
        if (!selectedEngineer.trim()) {
          await dialog.error("Please select a Engineer.");
          return;
        }

        // Go-live parity (2026-09-24, audit S4): send the EmpId; the server
        // resolves the address from Employees.EmailAddress.
        const ok = await sendEmailNewPart({
          toEmail: selectedEngineer.trim(),
          createdBy,
          partNum: partnum,
          revMajor,
          revMinor,
        });

        if (!ok) {
          await dialog.error("Could not send email to the engineer you have requested. If this problems persist, please contact Shawn Haley to resolve this e-mail issue.");
        }
      }

      await dialog.success("Successfully added part to database.");

      if (onFinish) {
        onFinish({
          partnum,
          revMajor,
          revMinor,
          createdBy,
          selectedEngineer,
          sendEmail,
        });
        return;
      }

      if (onBack) onBack();
    } catch (error) {
      console.error("frmSelect finish failed:", error);
      setPageError("Failed to finish engineer selection.");
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4 text-muted">Loading engineer selection...</CardBody>
      </Card>
    );
  }

  return (
    <div className="frm-select-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        compact
        icon="ri-user-star-line"
        eyebrow="MAFIA Aux"
        title="Notify Engineer"
        subtitle={
          <>
            Choose the responsible engineer for{" "}
            <strong style={{ color: "rgba(255,255,255,0.95)" }}>
              {partnum || "—"}
            </strong>{" "}
            Rev {revMajor}.{revMinor}
          </>
        }
        tone="violet"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back
          </Button>
        }
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="frm-select-toolbar-main">
                <div className="frm-select-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>

                <div className="frm-select-toolbar-main-right">
                  <InputGroup className="frm-select-engineer-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-user-star-line" />
                    </InputGroupText>
                    <Input
                      type="select"
                      value={selectedEngineer}
                      onChange={(e) => setSelectedEngineer(e.target.value)}
                      className="border-start-0"
                      disabled={!sendEmail || submitting}
                    >
                      <option value="">Select engineer</option>
                      {engineers.map((eng) => (
                        <option key={eng.empId} value={eng.empId}>
                          {eng.name && eng.name !== eng.empId ? `${eng.name} (${eng.empId})` : eng.empId}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </div>
              </div>

              <div className="frm-select-toolbar-utility">
                <Button
                  color="success"
                  className="frm-select-action-btn frm-select-utility-btn"
                  onClick={handleFinish}
                  disabled={submitting}
                >
                  <i className="ri-check-double-line frm-select-btn-icon me-1" />
                  {submitting ? "Finishing..." : "Finish"}
                </Button>

                <Badge color="light" className="frm-select-info-badge">
                  User: {currentUsername || "—"}
                </Badge>

                <div className="frm-select-info-pill">
                  <span className="frm-select-info-label">Part</span>
                  <span className="frm-select-info-value" title={partnum || "—"}>
                    {partnum || "—"}
                  </span>
                </div>

                <div className="frm-select-info-pill">
                  <span className="frm-select-info-label">Revision</span>
                  <span className="frm-select-info-value">
                    {revMajor}.{revMinor}
                  </span>
                </div>

                <div className="frm-select-info-pill">
                  <span className="frm-select-info-label">Engineers</span>
                  <span className="frm-select-info-value">{engineers.length}</span>
                </div>

                <div className="frm-select-info-pill">
                  <span className="frm-select-info-label">Send Email</span>
                  <span className="frm-select-info-value">{sendEmail ? "Yes" : "No"}</span>
                </div>
              </div>

              {pageError ? (
                <div className="frm-select-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <Row className="g-4">
                <Col xl={7}>
                  <div className="frm-select-panel-card h-100">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h5 className="mb-1">Verification Email Preference</h5>
                        <p className="text-muted mb-0">
                          Decide whether the selected engineer should receive an email notification.
                        </p>
                      </div>
                      <Badge color="light" className="frm-select-soft-badge text-uppercase">
                        Email Flow
                      </Badge>
                    </div>

                    <div className="frm-select-choice-grid">
                      <label className={`frm-select-choice-card ${sendEmail ? "active" : ""}`}>
                        <input
                          type="radio"
                          checked={sendEmail === true}
                          onChange={() => setSendEmail(true)}
                        />
                        <div>
                          <div className="frm-select-choice-title">Yes, send email</div>
                          <div className="frm-select-choice-copy">
                            Notify a verifying engineer right after finish.
                          </div>
                        </div>
                      </label>

                      <label className={`frm-select-choice-card ${!sendEmail ? "active" : ""}`}>
                        <input
                          type="radio"
                          checked={sendEmail === false}
                          onChange={() => setSendEmail(false)}
                        />
                        <div>
                          <div className="frm-select-choice-title">No email</div>
                          <div className="frm-select-choice-copy">
                            Complete the flow without sending a notification.
                          </div>
                        </div>
                      </label>
                    </div>

                    {sendEmail && (
                      <FormGroup className="mt-4 mb-0">
                        <div className="frm-select-panel-note">
                          Select the engineer who should verify the new part revision.
                        </div>
                      </FormGroup>
                    )}
                  </div>
                </Col>

                <Col xl={5}>
                  <div className="frm-select-panel-card h-100">
                    <h5 className="mb-3">Review Summary</h5>
                    <div className="frm-select-review-list">
                      <div className="frm-select-review-item">
                        <span>Created By</span>
                        <strong>{reviewSummary.createdBy}</strong>
                      </div>
                      <div className="frm-select-review-item">
                        <span>Send Email</span>
                        <strong>{reviewSummary.sendEmail}</strong>
                      </div>
                      <div className="frm-select-review-item">
                        <span>Selected Engineer</span>
                        <strong>{reviewSummary.selectedEngineer}</strong>
                      </div>
                      <div className="frm-select-review-item">
                        <span>Target Revision</span>
                        <strong>{reviewSummary.targetRevision}</strong>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <Button
                        color="success"
                        className="frm-select-action-btn"
                        onClick={handleFinish}
                        disabled={submitting}
                      >
                        <i className="ri-check-double-line frm-select-btn-icon me-1" />
                        {submitting ? "Finishing..." : "Finish"}
                      </Button>
                      <Button color="light" className="frm-select-action-btn border" onClick={onBack}>
                        <i className="ri-close-line frm-select-btn-icon me-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .frm-select-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .frm-select-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .frm-select-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .frm-select-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .frm-select-engineer-group {
          width: 100%;
        }

        .frm-select-panel-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
          height: 100%;
        }

        .frm-select-soft-badge {
          border: 1px solid #e9ebec;
          padding: 0.55rem 0.85rem;
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          border-radius: 999px;
        }

        .frm-select-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .frm-select-action-btn:disabled,
          .frm-select-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .frm-select-utility-btn {
            min-width: 110px;
          }

        .frm-select-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .frm-select-info-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid #e9ebec;
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #212529;
          background: #f8fafc;
        }

        .frm-select-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .frm-select-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .frm-select-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .frm-select-error-banner {
          display: flex;
          align-items: center;
          padding: 0.8rem 0.95rem;
          border-radius: 0.8rem;
          border: 1px solid rgba(220, 53, 69, 0.18);
          background: rgba(220, 53, 69, 0.06);
          color: #842029;
          font-weight: 500;
        }

        .frm-select-choice-grid {
          display: grid;
          gap: 0.9rem;
        }

        .frm-select-choice-card {
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
          border: 1px solid #dbe4ee;
          border-radius: 1rem;
          padding: 1rem;
          background: #fff;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .frm-select-choice-card:hover {
          background: #f8fbff;
          border-color: #b9d4ff;
        }

        .frm-select-choice-card.active {
          background: rgba(13, 110, 253, 0.08);
          border-color: rgba(13, 110, 253, 0.28);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .frm-select-choice-card input {
          margin-top: 0.2rem;
        }

        .frm-select-choice-title {
          font-weight: 700;
          color: #212529;
          margin-bottom: 0.2rem;
        }

        .frm-select-choice-copy {
          color: #878a99;
          font-size: 0.92rem;
        }

        .frm-select-review-list {
          display: grid;
          gap: 0.8rem;
        }

        .frm-select-review-item {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          align-items: center;
          padding: 0.8rem 0.9rem;
          border-radius: 0.9rem;
          background: #f8fafc;
          border: 1px solid #eef2f7;
        }

        .frm-select-review-item span {
          color: #878a99;
          font-size: 0.92rem;
        }

        .frm-select-review-item strong {
          color: #212529;
          text-align: right;
          word-break: break-word;
        }

        .frm-select-panel-note {
          color: #878a99;
          font-size: 0.92rem;
        }

        @media (max-width: 1199.98px) {
          .frm-select-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .frm-select-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .frm-select-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .frm-select-toolbar-utility {
            align-items: stretch;
          }

          .frm-select-review-item {
            align-items: flex-start;
            flex-direction: column;
          }

          .frm-select-review-item strong {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
}

async function getCurrentUsername() {
  return getMafiaEmpId();
}

async function getActiveEngineers() {
  const response = await api.get("mafia/active-engineers");
  return response.data;
}

async function sendEmailNewPart(request) {
  const response = await api.post("mafia/email-new-part", {
    toEmail: request.toEmail,
    createdBy: request.createdBy,
    partNum: request.partNum,
    revMajor: request.revMajor,
    revMinor: request.revMinor,
  });

  return response.data;
}